import React, { ReactNode, createContext, useContext, useState } from 'react';
import _ from 'lodash';
import { Form } from 'antd';
import moment from 'moment';
import { useParams } from 'react-router-dom';

import { useApplicationContext } from '@/context/v1/Application/context';
import type { Context, Methods, ReportParams, State } from '../types';
import type { GetRosaResponse } from '../hooks/useGetRosaData';
import { useSearchParams } from '@/hooks/v1/useSearchParams';
import type { BaseContext } from '@/types';

const { useForm } = Form;

const RosaReportContext = createContext<Context>({} as Context);

interface RosaReportProviderProps {
	children: ReactNode;
	reportData: GetRosaResponse;
}

export function RosaReportProvider({ reportData, children }: Readonly<RosaReportProviderProps>) {
	const [form] = useForm();
	const { file_id } = useParams<ReportParams>();
	const { organization, company } = useApplicationContext();
	const { organization_id = organization?.id, company_id = company?.id } = useSearchParams<BaseContext>();

	const [currentStep, setCurrentStep] = useState<number>(reportData.step);
	const [isNextStepBlocked, setIsNextStepBlocked] = useState<boolean>(false);

	function handleNextStep(): void {
		setCurrentStep(currentStep + 1);
	}

	function handlePreviousStep(): void {
		setCurrentStep(currentStep - 1);
	}

	function handleUpdateStep(step: number): void {
		setCurrentStep(step);
	}

	function handleBlockNextStep(blocked: boolean) {
		setIsNextStepBlocked(blocked);
	}

	const isCompleted = reportData.step === 5;

	const actionPlanFormatted = reportData?.action_plan?.reduce((previousActionPlans, currentActionPlan) => {
		return {
			...previousActionPlans,
			[currentActionPlan.category_name]: {
				...currentActionPlan,
				deadline: moment(currentActionPlan.deadline)
			}
		};
	}, {});

	function handleOnFormatInitialValues(values: GetRosaResponse) {
		let initialValues = {
			...values,
			action_plan: actionPlanFormatted,
			informations: {
				...values.informations,
				organization_id: organization_id,
				company_id: company_id
			}
		};

		if (values.informations.organization_id && values.informations.company_id) {
			_.set(initialValues, 'informations.organization_id', organization_id);
			_.set(initialValues, 'informations.company_id', company_id);
		}

		if (values.informations.analysis_date) {
			_.set(initialValues, 'informations.analysis_date', moment(values.informations.analysis_date));
		}

		if (values.informations.collection_date) {
			_.set(initialValues, 'informations.collection_date', moment(values.informations.collection_date));
		}

		return initialValues;
	}

	const initialValues = handleOnFormatInitialValues(reportData);

	const states: State = {
		report_id: reportData?.id,
		file_id,
		company_id,
		currentStep,
		isCompleted,
		organization_id,
		file: reportData.file,
		isNextStepBlocked,
		actionPlans: actionPlanFormatted || {},
		isConsolidated: reportData.consolidated
	};

	const methods: Methods = {
		onNextStep: handleNextStep,
		onUpdateStep: handleUpdateStep,
		onPreviousStep: handlePreviousStep,
		onBlockNextStep: handleBlockNextStep
	};

	const context = {
		...states,
		...methods
	};

	return (
		<RosaReportContext.Provider value={context}>
			<Form initialValues={initialValues} form={form}>
				{children}
			</Form>
		</RosaReportContext.Provider>
	);
}

export function useRosaContext() {
	const context = useContext(RosaReportContext);
	return context;
}
